import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, Video, Building, ArrowRight, ArrowLeft, Clock, 
  X, FileText, CheckCircle, AlertCircle, Loader2,
  User, Phone, Mail, FileUp, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { useToast } from '../hooks/use-toast';
import { BRAND, SERVICES } from '../data/mock';
import { getAvailableSlots, createAppointment, getPresignedUploadUrl, uploadFileToS3, createRazorpayOrder, verifyRazorpayPayment } from '../services/api';

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Multi-step form state
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // Form data
  const [consultationType, setConsultationType] = useState(
    searchParams.get('type') === 'offline' ? 'OFFLINE' : 'ONLINE'
  );
  const [offlineVenue, setOfflineVenue] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(null);
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    reason: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [paymentOption, setPaymentOption] = useState('pay_now');
  const isOfflineFlow = consultationType === 'OFFLINE';
  const totalSteps = isOfflineFlow ? 5 : 4;
  const stepLabels = isOfflineFlow
    ? ['Type', 'Clinic', 'Date & Time', 'Details', 'Confirm']
    : ['Type', 'Date & Time', 'Details', 'Confirm'];

  // Fetch slots when date changes
  useEffect(() => {
    if (!selectedDate) return;
    if (consultationType === 'OFFLINE' && !offlineVenue) return;
    fetchAvailableSlots(selectedDate);
  }, [selectedDate, consultationType, offlineVenue]);

  useEffect(() => {
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setSlotsError(null);

    if (consultationType !== 'OFFLINE') {
      setOfflineVenue(null);
    }
  }, [consultationType]);

  const fetchAvailableSlots = async (date) => {
    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);

    try {
      // Format date in local timezone to avoid timezone conversion issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const result = await getAvailableSlots(dateStr, consultationType, offlineVenue);
      
      if (result.is_closed) {
        setSlotsError(result.message || 'Clinic is closed on this day');
        setAvailableSlots([]);
      } else {
        setAvailableSlots(result.slots || []);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlotsError('Failed to fetch available slots. Please try again.');
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  // Disable Tuesdays, Thursdays and past dates
  const disabledDays = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 2 || date.getDay() === 4; // Tuesday = 2, Thursday = 4
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/tiff'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a supported file type.`,
          variant: "destructive"
        });
        return false;
      }
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit.`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });

    if (uploadedFiles.length + validFiles.length > 5) {
      toast({
        title: "Maximum files exceeded",
        description: "You can upload a maximum of 5 files.",
        variant: "destructive"
      });
      return;
    }

    // Get presigned URLs, upload files to S3, then store metadata
    for (const file of validFiles) {
      try {
        const presignedResult = await getPresignedUploadUrl(file.name, file.type);
        // Actually upload the file to S3
        await uploadFileToS3(presignedResult.upload_url, file);
        setUploadedFiles(prev => [...prev, {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          s3_key: presignedResult.s3_key,
          upload_url: presignedResult.upload_url
        }]);
        toast({
          title: "File uploaded",
          description: `${file.name} uploaded successfully.`
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        toast({
          title: "Upload error",
          description: `Failed to upload ${file.name}. Please try again.`,
          variant: "destructive"
        });
      }
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateStep = (currentStep) => {
    const slotSelectionStep = isOfflineFlow ? 3 : 2;
    const detailsStep = isOfflineFlow ? 4 : 3;
    switch (currentStep) {
      case 1:
        return consultationType !== null;
      case 2:
        return consultationType === 'ONLINE' || offlineVenue !== null;
      case slotSelectionStep:
        return selectedDate !== null && selectedSlot !== null;
      case detailsStep:
        return formData.patient_name.trim() !== '' && 
               formData.patient_email.trim() !== '' && 
               formData.patient_phone.trim() !== '' &&
               formData.reason.trim() !== '';
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      toast({
        title: "Please complete all required fields",
        variant: "destructive"
      });
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const shouldShowVenueStep = step === 2 && isOfflineFlow;
  const slotSelectionStep = isOfflineFlow ? 3 : 2;
  const detailsStep = isOfflineFlow ? 4 : 3;
  const confirmStep = totalSteps;

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleRazorpayPayment = async (bookingId, accessToken) => {
    try {
      // Create Razorpay order
      const orderData = await createRazorpayOrder(bookingId);
      
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "JB's Homoeopathy",
        description: `Consultation Fee - ${consultationType === 'ONLINE' ? 'Online' : 'In-Clinic'}`,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.patient_name,
          email: orderData.patient_email,
          contact: orderData.patient_phone
        },
        theme: {
          color: '#2F5233'
        },
        handler: async function (response) {
          try {
            // Verify payment
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              appointment_id: bookingId
            });
            
            toast({
              title: "Payment Successful!",
              description: "Your booking has been confirmed."
            });
            
            navigate(`/booking-success?id=${bookingId}&payment=success&token=${accessToken}`);
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast({
              title: "Payment Verification Failed",
              description: "Please contact support if amount was deducted.",
              variant: "destructive"
            });
          }
        },
        modal: {
          ondismiss: function() {
            toast({
              title: "Payment Cancelled",
              description: "Your booking is saved. You can pay later from dashboard.",
            });
            navigate(`/booking-success?id=${bookingId}&token=${accessToken}`);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast({
        title: "Payment Error",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      // Prepare uploaded reports data
      const uploadedReports = uploadedFiles.map(f => ({
        file_name: f.name,
        file_type: f.type,
        file_size: f.size,
        s3_key: f.s3_key
      }));
      
      // Create appointment
      const appointmentData = {
        patient_name: formData.patient_name,
        patient_email: formData.patient_email,
        patient_phone: formData.patient_phone,
        consultation_type: consultationType,
        venue: consultationType === 'OFFLINE' ? offlineVenue : null,
        appointment_date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
        time_slot: selectedSlot.time,
        reason: formData.reason,
        uploaded_reports: uploadedReports,
        payment_option: paymentOption
      };
      
      const result = await createAppointment(appointmentData);
      
      // If online consultation OR pay_now, initiate Razorpay payment
      if (consultationType === 'ONLINE' || paymentOption === 'pay_now') {
        toast({
          title: "Booking Created!",
          description: "Opening payment gateway..."
        });
        
        setIsLoading(false);
        await handleRazorpayPayment(result.booking_id, result.access_token);
      } else {
        // Pay later option - redirect to success page
        toast({
          title: "Booking Submitted!",
          description: "Your appointment request has been submitted."
        });
        
        navigate(`/booking-success?id=${result.booking_id}&token=${result.access_token}`);
      }

    } catch (error) {
      console.error('Error creating appointment:', error);
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || "Failed to create booking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedService = SERVICES.find(
    s => (consultationType === 'ONLINE' && s.icon === 'video') || 
         (consultationType === 'OFFLINE' && s.icon === 'building')
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF9F5] to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-serif font-bold text-[#2F5233]">Book Your Consultation</h1>
          <p className="text-gray-600 mt-2">with {BRAND.doctorName}</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-10">
          <div className="flex items-center justify-center">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s, index) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-medium transition-all duration-300 ${
                  step >= s 
                    ? 'bg-[#2F5233] text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {index < totalSteps - 1 && (
                  <div className={`w-20 h-1 mx-2 transition-all duration-300 ${
                    step > s ? 'bg-[#2F5233]' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 px-4">
            {stepLabels.map((label) => (
              <span key={label} className="text-xs text-gray-500">{label}</span>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="shadow-xl border-0">
          <CardContent className="p-8">
            {/* Step 1: Consultation Type */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 font-serif">Choose Consultation Type</h2>
                  <p className="text-gray-500 mt-1">Select how you'd like to consult with the doctor</p>
                </div>
                
                <RadioGroup 
                  value={consultationType} 
                  onValueChange={setConsultationType}
                  className="grid md:grid-cols-2 gap-6"
                >
                  {SERVICES.map((service) => {
                    const type = service.icon === 'video' ? 'ONLINE' : 'OFFLINE';
                    return (
                      <Label
                        key={service.id}
                        htmlFor={type}
                        className={`cursor-pointer transition-all duration-300 ${
                          consultationType === type 
                            ? 'ring-2 ring-[#E91E8C] ring-offset-2' 
                            : 'hover:shadow-lg'
                        }`}
                      >
                        <Card className="border-2 border-transparent h-full">
                          <CardContent className="p-6">
                            <div className="flex items-start space-x-4">
                              <RadioGroupItem value={type} id={type} className="mt-1" />
                              <div className="flex-1">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                                  service.icon === 'video' ? 'bg-blue-100' : 'bg-emerald-100'
                                }`}>
                                  {service.icon === 'video' ? (
                                    <Video className="w-6 h-6 text-blue-600" />
                                  ) : (
                                    <Building className="w-6 h-6 text-emerald-600" />
                                  )}
                                </div>
                                <h3 className="font-semibold text-gray-900">{service.title}</h3>
                                <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                                <p className="text-xl font-bold text-[#2F5233] mt-3">₹{service.price.toLocaleString()}</p>
                                <p className="text-sm text-gray-500">{service.duration}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </div>
            )}

            {/* Step 2: Clinic selection for offline bookings */}
            {shouldShowVenueStep && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 font-serif">Choose Clinic Location</h2>
                  <p className="text-gray-500 mt-1">Select the clinic you would like to visit</p>
                </div>

                <RadioGroup
                  value={offlineVenue || ''}
                  onValueChange={(value) => {
                    setOfflineVenue(value);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                    setAvailableSlots([]);
                    setSlotsError(null);
                  }}
                  className="grid md:grid-cols-2 gap-4"
                >
                  <Label
                    htmlFor="HEAL_STUDIO_116"
                    className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                      offlineVenue === 'HEAL_STUDIO_116' ? 'border-[#E91E8C] bg-[#E91E8C]/5' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value="HEAL_STUDIO_116" id="HEAL_STUDIO_116" className="mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Heal Studio, Sector 116</p>
                        <a
                          href="https://www.google.com/maps/dir//Dr+Jenice+Bhatia+Homeopathic+Clinic,+H18+(Basement,+Community+Center+Rd,+Sector+116,+Noida,+Uttar+Pradesh+201301/@28.5222018,77.4018255,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x390cef25a44351d3:0x13a8bb6d2c258461!2m2!1d77.3989467!2d28.5658982?hl=en-IN&entry=ttu&g_ep=EgoyMDI2MDkwMi4wIKXMDSoASAFQAw%3D%3D"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block text-sm text-[#2F5233] hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          H18 (Basement), Community Center Rd, Sector 116, Noida, Uttar Pradesh 201301
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Mon-Fri: 11:00 AM-2:00 PM</p>
                        <p className="text-sm text-gray-500">Sat-Sun: 11:00 AM-3:00 PM</p>
                      </div>
                    </div>
                  </Label>

                  <Label
                    htmlFor="SECTOR_29"
                    className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                      offlineVenue === 'SECTOR_29' ? 'border-[#E91E8C] bg-[#E91E8C]/5' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value="SECTOR_29" id="SECTOR_29" className="mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Sector 29 (Original Address)</p>
                        <p className="text-sm text-gray-500 mt-1">Mon-Sun: 4:00 PM-7:00 PM</p>
                        <p className="text-sm text-red-500">Closed on Tuesday and Thursday</p>
                      </div>
                    </div>
                  </Label>
                </RadioGroup>
              </div>
            )}

            {/* Step 2 (online) / Step 3 (offline): Date & Time Selection */}
            {step === slotSelectionStep && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 font-serif">Select Date & Time</h2>
                  <p className="text-gray-500 mt-1">Choose your preferred appointment slot</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <Label className="mb-3 block font-medium">Select Date</Label>
                    <div className="border rounded-xl p-4 bg-white">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={disabledDays}
                        className="rounded-md"
                      />
                    </div>
                    <div className="mt-3 flex items-center space-x-2 text-sm text-gray-500">
                      <Info className="w-4 h-4 text-[#F5A623]" />
                      <span>Clinic closed on Tuesdays and Thursdays</span>
                    </div>
                  </div>
                  
                  {/* Time Slots */}
                  <div>
                    <Label className="mb-3 block font-medium">Select Time</Label>
                    {slotsLoading ? (
                      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-xl">
                        <Loader2 className="w-6 h-6 animate-spin text-[#2F5233]" />
                        <span className="ml-2 text-gray-500">Loading slots...</span>
                      </div>
                    ) : selectedDate ? (
                      slotsError ? (
                        <div className="bg-red-50 text-red-600 p-4 rounded-xl text-center">
                          <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                          <p>{slotsError}</p>
                        </div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {availableSlots.map((slot) => (
                            <Button
                              key={slot.time}
                              variant={selectedSlot?.time === slot.time ? "default" : "outline"}
                              className={`h-14 ${
                                selectedSlot?.time === slot.time 
                                  ? 'bg-[#2F5233] hover:bg-[#2F5233]/90' 
                                  : slot.available 
                                    ? 'hover:border-[#E91E8C] hover:text-[#E91E8C]' 
                                    : 'opacity-50 cursor-not-allowed'
                              }`}
                              disabled={!slot.available}
                              onClick={() => setSelectedSlot(slot)}
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              {slot.display_time}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-xl text-center">
                          <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                          <p>No available slots on this date</p>
                        </div>
                      )
                    ) : consultationType === 'OFFLINE' && !offlineVenue ? (
                      <div className="bg-gray-50 text-gray-500 p-8 rounded-xl text-center">
                        <Building className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Please select a clinic venue first</p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 text-gray-500 p-8 rounded-xl text-center">
                        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Please select a date first</p>
                      </div>
                    )}
                    
                    {selectedSlot && (
                      <div className="mt-4 p-4 bg-[#2F5233]/5 rounded-xl">
                        <p className="text-sm text-[#2F5233] font-medium">
                          Selected: {selectedDate?.toLocaleDateString('en-IN', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })} at {selectedSlot.display_time}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Patient Details */}
            {step === detailsStep && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 font-serif">Patient Details</h2>
                  <p className="text-gray-500 mt-1">Fill in your information for the appointment</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="patient_name"
                        name="patient_name"
                        value={formData.patient_name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="patient_phone">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="patient_phone"
                        name="patient_phone"
                        value={formData.patient_phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="patient_email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="patient_email"
                      name="patient_email"
                      type="email"
                      value={formData.patient_email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Consultation *</Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Please describe your symptoms or health concerns..."
                    rows={4}
                    required
                  />
                </div>
                
                {/* File Upload */}
                <div className="space-y-3">
                  <Label>Upload Medical Reports (Optional)</Label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-[#E91E8C]/50 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.tiff"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <FileUp className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">Drop files here or click to upload</p>
                      <p className="text-sm text-gray-400 mt-1">PDF, JPG, PNG, TIFF (Max 5 files, 10MB each)</p>
                    </label>
                  </div>
                  
                  {/* Uploaded Files List */}
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-[#2F5233]" />
                            <div>
                              <p className="text-sm font-medium text-gray-700">{file.name}</p>
                              <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Final Step: Review & Pay */}
            {step === confirmStep && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 font-serif">Review & Confirm</h2>
                  <p className="text-gray-500 mt-1">Please review your booking details</p>
                </div>
                
                {/* Summary Card */}
                <div className="bg-gradient-to-br from-[#2F5233]/5 to-[#E91E8C]/5 rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b border-[#2F5233]/10">
                    <div className="flex items-center space-x-3">
                      {consultationType === 'ONLINE' ? (
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Video className="w-6 h-6 text-blue-600" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Building className="w-6 h-6 text-emerald-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900">
                          {consultationType === 'ONLINE' ? 'Online Consultation' : 'In-Clinic Visit'}
                        </p>
                        <p className="text-sm text-gray-500">{selectedService?.duration}</p>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-[#2F5233]">
                      ₹{selectedService?.price.toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Date & Time</p>
                      <p className="font-medium text-gray-900">
                        {selectedDate?.toLocaleDateString('en-IN', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}, {selectedSlot?.display_time}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Patient Name</p>
                      <p className="font-medium text-gray-900">{formData.patient_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{formData.patient_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{formData.patient_phone}</p>
                    </div>
                  </div>
                  
                  {formData.reason && (
                    <div>
                      <p className="text-sm text-gray-500">Reason</p>
                      <p className="text-gray-900">{formData.reason}</p>
                    </div>
                  )}
                  
                  {uploadedFiles.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Uploaded Reports</p>
                      <p className="text-gray-900">{uploadedFiles.length} file(s)</p>
                    </div>
                  )}
                </div>
                
                {/* Payment Options (Only for In-Clinic) */}
                {consultationType === 'OFFLINE' && (
                  <div className="space-y-3">
                    <Label className="font-medium">Payment Option</Label>
                    <RadioGroup value={paymentOption} onValueChange={setPaymentOption}>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Label
                          htmlFor="pay_now"
                          className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                            paymentOption === 'pay_now' 
                              ? 'border-[#E91E8C] bg-[#E91E8C]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="pay_now" id="pay_now" />
                            <div>
                              <p className="font-medium">Pay Now</p>
                              <p className="text-sm text-gray-500">Pay online to confirm</p>
                            </div>
                          </div>
                        </Label>
                        <Label
                          htmlFor="pay_later"
                          className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                            paymentOption === 'pay_later' 
                              ? 'border-[#E91E8C] bg-[#E91E8C]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem value="pay_later" id="pay_later" />
                            <div>
                              <p className="font-medium">Pay at Clinic</p>
                              <p className="text-sm text-gray-500">Pay during your visit</p>
                            </div>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
                
                {consultationType === 'ONLINE' && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-900">Payment Required for Online Consultation</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Online consultations require payment to confirm your booking. 
                          You'll receive the video call link after payment.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div></div>
              )}
              
              {step < totalSteps ? (
                <Button 
                  onClick={nextStep}
                  className="bg-[#2F5233] hover:bg-[#2F5233]/90"
                  disabled={!validateStep(step)}
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  className="bg-[#E91E8C] hover:bg-[#E91E8C]/90 min-w-[180px]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {consultationType === 'ONLINE' || paymentOption === 'pay_now' 
                        ? 'Proceed to Payment' 
                        : 'Confirm Booking'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;
